/**
 * A supplier order in the chat: the basket card the owner approves, and then
 * the same card following the order live (docs/supplier-ordering-plan.md,
 * "How an order works").
 *
 * 🚨 Every figure here was worked out by the server (`basket.ts`) from prices
 * checked on the merchant's own pages. The model chose which tool to call and
 * nothing else; it never writes a line, a price or the ceiling.
 *
 * 🚨 Approving here does NOT spend anything. Approve opens pay.mucka.ai, where
 * the owner approves exactly this ceiling at this merchant with a passkey.
 * That passkey is the second gate, and the only thing that lets money move.
 *
 * The block stores the basket as it was when the card was made. What happens
 * next (approved, running, a question, the order number) is read live from
 * `GET /api/supplier-orders/<id>`, because a stored reply cannot change.
 *
 * Pure and directive-free (anti-pattern #18): the panel, the Mucka Bar and the
 * oversight grader all import it.
 */

export interface SupplierOrderLine {
  /** What the owner asked for, as they said it. */
  requested: string;
  /** The product as the merchant names it. */
  description: string;
  code: string | null;
  url: string | null;
  quantity: number;
  unitPence: number;
  pack: string | null;
}

export interface SupplierOrderBlock {
  kind: 'supplier-order';
  orderId: string;
  merchant: string;
  fulfilment: 'collect' | 'delivery';
  /** The branch with its address, or the delivery address. */
  destination: string;
  lines: SupplierOrderLine[];
  subtotalPence: number;
  /** Room for a price that moved since the search. */
  allowancePence: number;
  deliveryPence: number;
  /** The most the owner approves. Above it, the run stops and asks again. */
  ceilingPence: number;
  /** "Visa ending 4242". */
  card: string;
  /** Off production: goes to the payment step and stops, buying nothing. */
  rehearsal: boolean;
  /** The step of the order this card was posted for (order-ended.ts). Absent
   *  on the card order_parts drew, which is the 'prepared' one. */
  stage?: OrderStage;
}

/** Where an order is, as the chat follows it: each step gets its own card. */
export type OrderStage = 'prepared' | 'placing' | 'bank' | 'question' | 'done';

export type SupplierOrderStatus =
  | 'awaiting_approval' | 'approved' | 'running' | 'needs_answer'
  | 'ordered' | 'rehearsed' | 'stopped' | 'failed' | 'cancelled';

export interface SupplierOrderQuestion {
  requestId: string;
  /** 'bank_app': the bank wants approval in its own app; Mucka has replied. */
  kind?: string;
  question: string;
  expiresAt: string | null;
  fields: Array<{ key: string; label: string; required: boolean; protected: boolean; boolean?: boolean }>;
}

/** What `GET /api/supplier-orders/<id>` returns: the live half of the card. */
export interface SupplierOrderState {
  status: SupplierOrderStatus;
  question: SupplierOrderQuestion | null;
  merchantOrderId: string | null;
  totalPence: number | null;
  stopMessage: string | null;
  /** What the placed order became; absent until it has been booked. */
  booked?: OrderBooked | null;
}

/**
 * What a placed order became (`supplier-orders/book.ts`): on the job's costs,
 * its parts marked ordered, and which card is waiting in Actions.
 */
export interface OrderBooked {
  onJob: boolean;
  partsOrdered: number;
  partsAdded: number;
  /** bill: "Add to accounts" · job_card: "Put on a job" · already: that receipt was logged. */
  accounts: 'bill' | 'job_card' | 'already' | 'none';
}

/** What Mucka says the order became, or '' before it has been booked. */
export function bookedLine(booked: OrderBooked | null | undefined): string {
  if (!booked) return '';
  const said: string[] = [];
  if (booked.onJob) said.push(booked.partsOrdered + booked.partsAdded > 0 ? "It's on the job's costs, and the parts are marked ordered." : "It's on the job's costs.");
  if (booked.accounts === 'bill') said.push('A bill is ready to approve for your accounts.');
  if (booked.accounts === 'job_card') said.push('Say which job it was for on the card in Actions.');
  return said.join(' ');
}

export const FINAL_ORDER_STATUSES: ReadonlySet<SupplierOrderStatus> = new Set([
  'ordered', 'rehearsed', 'stopped', 'failed', 'cancelled',
]);

export function poundsFromPence(pence: number): string {
  return `£${(pence / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function lineLabel(l: SupplierOrderLine): string {
  return `${l.quantity} × ${l.description}${l.pack ? ` (${l.pack.toLowerCase()})` : ''}`;
}

export function whereLabel(block: Pick<SupplierOrderBlock, 'fulfilment' | 'destination'>): string {
  return block.fulfilment === 'collect' ? `Collect from ${block.destination}` : `Deliver to ${block.destination}`;
}

/** The one line under the card's heading: where the order has got to. */
export function statusLine(block: SupplierOrderBlock, state: SupplierOrderState | null): string {
  switch (state?.status ?? 'awaiting_approval') {
    case 'awaiting_approval':
      return `Nothing is bought until you approve up to ${poundsFromPence(block.ceilingPence)} with your passkey.`;
    case 'approved':
      return 'Approved. Starting the order…';
    case 'running':
      if (state?.question?.kind === 'bank_app') return 'Approve the payment in your bank app now. Mucka carries on as soon as your bank confirms.';
      return `Placing the order at ${block.merchant}. This usually takes three or four minutes.`;
    case 'needs_answer':
      return 'It needs an answer from you to carry on.';
    case 'ordered':
      return [
        state?.merchantOrderId ? `Ordered: ${block.merchant} order ${state.merchantOrderId}` : 'Ordered',
        state?.totalPence != null ? `, ${poundsFromPence(state.totalPence)}` : '',
        '.',
        state?.booked ? ` ${bookedLine(state.booked)}` : '',
      ].join('').trimEnd();
    case 'rehearsed':
      return state?.stopMessage ?? 'A rehearsal: it reached the payment step and stopped, so nothing was bought.';
    case 'stopped':
    case 'failed':
      return state?.stopMessage ?? 'It stopped before anything was bought.';
    case 'cancelled':
      return state?.stopMessage ?? 'Cancelled. Nothing was bought.';
  }
}

/** One line for the oversight transcript. No addresses: a branch can be a home. */
export function summariseSupplierOrderBlock(block: SupplierOrderBlock): string {
  return `[supplier-order: ${block.merchant}, ${block.lines.length} line${block.lines.length === 1 ? '' : 's'}, `
    + `up to ${poundsFromPence(block.ceilingPence)}, ${block.fulfilment}, approve with passkey${block.rehearsal ? ', rehearsal' : ''}]`;
}

/** What the conversation's model is told the card shows. */
export function supplierOrderToolNote(block: SupplierOrderBlock, opts: { voice?: boolean } = {}): string {
  return [
    `The order card is on screen: ${block.lines.length} line${block.lines.length === 1 ? '' : 's'} from ${block.merchant}, `
      + `${block.fulfilment === 'collect' ? 'for collection' : 'for delivery'}, up to ${poundsFromPence(block.ceilingPence)} on the ${block.card}.`,
    'NOTHING has been ordered or paid. They approve it on the card with their passkey; you cannot approve it for them.',
    'Say it is ready to check and approve in one short sentence. Do not read the lines or prices back: they are on the card.',
    ...(block.rehearsal ? ['This is not the live app, so it is a rehearsal: it will stop at the payment step and buy nothing.'] : []),
    // Tom's first real voice order, 3 Nov 2026: he went to approve, Mucka
    // asked "Still there?", heard a "No." meant for nobody, said goodnight and
    // hung up, and the order ran with nobody on the line to hear how it went.
    ...(opts.voice ? [
      'Approving takes them to a passkey page for a minute or two, with this call still open behind it. While they are there they cannot talk to you.',
      'So until they speak to you again: say nothing, do not ask whether they are still there, and NEVER end the call. A word you hear meanwhile is them using that page, not an answer to you.',
      'When they come back, say the order is being placed and the card will show when the merchant takes it. You will be told how it ends.',
    ] : []),
  ].join('\n');
}
